import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@/lib/utils";
import { ScrollAreaSafe } from "./scroll-area";
import { Close } from "@/assets";
import { HeadingSection } from "./typography";
import { usePortalContainer } from "@/contexts/portal";

// Dialog Context
interface DialogContextValue {
    lastDialogOpenState?: boolean;
    setLastOpenDialogState: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextValue | undefined>(
    undefined,
);

const useDialogContext = () => {
    const context = React.useContext(DialogContext);
    if (context === undefined) {
        throw new Error(
            "useDialogContext must be used within a DialogProvider",
        );
    }
    return context;
};

const DialogProvider = ({ children }: { children: React.ReactNode }) => {
    // This state is intended to be read-only; it is not supposed to be used
    // to change the state of the dialog, but rather be the latest accurate
    // representation of the state of the dialog (who's state is internal and
    // cannot be accessed directly)
    const [lastDialogOpenState, setLastOpenDialogState] = React.useState<
        boolean | undefined
    >();

    return (
        <DialogContext.Provider
            value={{ lastDialogOpenState, setLastOpenDialogState }}
        >
            {children}
        </DialogContext.Provider>
    );
};

const Dialog = ({
    open,
    onOpenChange,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => (
    <DialogProvider>
        <InternalDialogToAccessDialogContext
            open={open}
            onOpenChange={onOpenChange}
            {...props}
        />
    </DialogProvider>
);
Dialog.displayName = DialogPrimitive.Root.displayName;

const InternalDialogToAccessDialogContext = ({
    open,
    onOpenChange,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => {
    const { lastDialogOpenState, setLastOpenDialogState } = useDialogContext();

    React.useEffect(() => {
        if (open !== undefined && lastDialogOpenState !== open) {
            setLastOpenDialogState(open);
        }
    }, [open, lastDialogOpenState, setLastOpenDialogState]);
    return (
        <DialogPrimitive.Root
            open={open}
            onOpenChange={(nextOpen) => {
                setLastOpenDialogState(nextOpen);
                onOpenChange?.(nextOpen);
            }}
            {...props}
        />
    );
};

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
    <DialogPrimitive.Overlay
        className={cn(
            "bg-blanket-default data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50",
            className,
        )}
        {...props}
    />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

function getComponentDisplayName<P>(element: P): string | undefined {
    if (
        React.isValidElement(element) &&
        // Fragments/portals have symbol types, on which `in` throws
        (typeof element.type === "function" ||
            typeof element.type === "object") &&
        element.type !== null &&
        "displayName" in element.type &&
        typeof element.type.displayName === "string"
    ) {
        return element.type.displayName;
    }

    return undefined;
}

const DialogContent = ({
    className,
    children,
    overlayClassname = "",
    unmountContentWhenClosed = true,
    hideCloseButton = false,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
    overlayClassname?: string;
    unmountContentWhenClosed?: boolean;
    hideCloseButton?: boolean;
}) => {
    const { lastDialogOpenState } = useDialogContext();

    const dialogHeader = React.Children.toArray(children).find(
        (child) => getComponentDisplayName(child) === DialogHeader.displayName,
    );
    const dialogFooter = React.Children.toArray(children).find(
        (child) => getComponentDisplayName(child) === DialogFooter.displayName,
    );
    const childrenWithoutHeaderAndFooter = React.Children.toArray(
        children,
    ).filter(
        (child) =>
            ![DialogHeader.displayName, DialogFooter.displayName].includes(
                getComponentDisplayName(child) ?? "",
            ),
    );

    const portalContainer = usePortalContainer();

    return (
        <DialogPortal container={portalContainer}>
            <DialogOverlay className={overlayClassname} />
            <DialogPrimitive.Content
                className={cn(
                    "rounded-default bg-elevation-surface-default px-wider py-wide shadow-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid max-h-[75%] w-full max-w-[600px] translate-x-[-50%] translate-y-[-50%] grid-rows-[auto_1fr_auto] duration-200",
                    className,
                )}
                {...props}
            >
                {dialogHeader}
                <ScrollAreaSafe>
                    {unmountContentWhenClosed && lastDialogOpenState === false
                        ? null
                        : childrenWithoutHeaderAndFooter}
                </ScrollAreaSafe>
                {dialogFooter}
                {!hideCloseButton && (
                    <DialogPrimitive.Close className="rounded-default bg-neutral-subtlest-default hover:bg-neutral-subtlest-hover focus:ring-ring active:bg-neutral-subtlest-press absolute top-6 right-6 cursor-pointer focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
                        <Close />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
};
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn("space-y-narrow pb-wide flex flex-col", className)}
        {...props}
    />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "pt-wide flex flex-col-reverse sm:flex-row sm:justify-end",
            className,
        )}
        {...props}
    />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
    className,
    children,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
    <DialogPrimitive.Title
        asChild
        // padding is to avoid clash with dialog close button
        className={cn("h-fit pr-10", className)}
        {...props}
    >
        <HeadingSection>{children}</HeadingSection>
    </DialogPrimitive.Title>
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
    <DialogPrimitive.Description
        className={cn("text-default text-subtle", className)}
        {...props}
    />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogClose,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
};
